import { useCallback, useEffect, useMemo, useState, type FunctionComponent } from "react";
import { FileAddOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { productClientActions, productsActions, RequestStatus } from "@fitness-app/app-store";
import { OrderStatusOption, type Order } from "@fitness-app/data-models/entities/Order";
import {
  OrderPaymentTypeOption,
  PaymentTypeOption,
  ProductChargeType,
  ProductStatus,
  type Product,
  type ProductPrice,
} from "@fitness-app/data-models/entities/Product";
import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { getErrorMessage } from "@fitness-app/utils";
import { applyCouponsToAmount, couponAppliesToRecurring } from "@fitness-app/utils/src/discounts/couponMath";
import { getHumanPrice } from "@fitness-app/utils/src/products/getHumanPrice";

import InvoiceDataInfo from "~/modules/Products/components/InvoiceDataInfo/InvoiceDataInfo";
import {
  buildCustomPricePayload,
  customPriceDefaults,
  CUSTOM_PRICE_OPTION,
  getProductCurrency,
  isCustomPriceSelected,
  ManualOrderCustomPriceFields,
  resolveSkipImpactOnClientAccess,
  type ManualOrderCustomPriceFormModel,
} from "~/modules/Products/components/ManualOrderCustomPrice/ManualOrderCustomPrice";
import InvoiceNumberInfo from "~/modules/Products/components/InvoiceNumberInfo/InvoiceNumberInfo";
import { getProductPrice } from "~/modules/Products/hooks/useProductTaskAutomation";
import CustomConsentData from "~/modules/Products/ProductClientDetails/ProductClientActionsHistory/ClientCustomConsents";
import { toOrderPaymentDescriptor } from "~/modules/Products/helpers/describeOrderPayment";
import { OrderPaidAtCell } from "~/modules/Products/helpers/OrderPaidAtCell";
import { OrderPaymentCell } from "~/modules/Products/helpers/OrderPaymentCell";
import { OrderStatusCell } from "~/modules/Products/helpers/OrderStatusCell";
import { productApi, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { CrossSellInfo } from "../../components/CrossSellInfo/CrossSellInfo";
import IssueInvoice from "../../Product/ProductOrders/IssueInvoice";

interface OwnProps {
  productClient: ProductClient;
  ownerView?: boolean;
  /**
   * Faktury, dane do faktury i wystawianie zamówień. Kwoty i kody rabatowe
   * zostają widoczne zawsze — trener ma widzieć, czy klientka płaci i ile.
   */
  showBillingData?: boolean;
  /** Ogranicza listę do najnowszych N zamówień (widok skrócony dla trenera). */
  maxRows?: number;
}

type Props = OwnProps;

interface ManualOrderFormModel extends ManualOrderCustomPriceFormModel {
  productId: string;
  priceId: string;
  customLabel: string;
  amount: number;
  sendClientNotification: boolean;
}

const getPriceAmount = (price: ProductPrice) => (price.unit_amount ?? 0) / 100;

const getActivePaidPrices = (product: Pick<Product, "prices">): ProductPrice[] =>
  (product.prices || []).filter(
    (price): price is ProductPrice => price.active && typeof price.unit_amount === "number",
  );

const ProductClientActionsHistory: FunctionComponent<Props> = ({
  productClient,
  ownerView,
  showBillingData = true,
  maxRows,
}) => {
  const { t } = useTranslation(["products", "client", "common"]);
  const dispatch = useAppDispatch();
  const [showManualOrderForm, setShowManualOrderForm] = useState(false);
  const [issuingManualOrder, setIssuingManualOrder] = useState(false);
  const [manualOrderForm] = Form.useForm<ManualOrderFormModel>();
  const userDetails = useAppSelector((store) => store.user.profile);
  const { clientOrders, ordersStatus, ordersPage } = useAppSelector((store) => store.productClient);
  const data = useAppSelector((store) => store.productsSettings.data);
  const productDetails = useAppSelector((store) => store.product.details);
  const { productsListData, productsListStatus } = useAppSelector((store) => store.products);

  useEffect(() => {
    if (ownerView && showBillingData && productsListStatus === null) {
      dispatch(productsActions.subscribeToProducts());
    }
  }, [dispatch, ownerView, showBillingData, productsListStatus]);

  const clientEmail = ownerView ? productClient.email : userDetails?.email;
  const customConsents =
    data?.checkoutSettings?.cart?.pl?.customConsents?.filter(
      (consent) => consent.applyToProducts === true || consent.applyToProducts?.includes(productClient.productId),
    ) || [];

  const productOptions = useMemo(
    () =>
      productsListData
        .filter((product) => product.chargeType === ProductChargeType.PAID && product.status === ProductStatus.ACTIVE)
        .filter((product) => getActivePaidPrices(product).length > 0)
        .map((product) => ({ label: product.name, value: product.id })),
    [productsListData],
  );

  const selectedManualOrderProductId = Form.useWatch("productId", manualOrderForm);

  const selectedProductForManualOrder = useMemo(() => {
    if (!selectedManualOrderProductId) {
      return null;
    }

    if (productDetails?.id === selectedManualOrderProductId) {
      return productDetails;
    }

    return productsListData.find((product) => product.id === selectedManualOrderProductId) ?? null;
  }, [selectedManualOrderProductId, productDetails, productsListData]);

  const manualOrderPrices = useMemo(
    () => (selectedProductForManualOrder ? getActivePaidPrices(selectedProductForManualOrder) : []),
    [selectedProductForManualOrder],
  );

  const manualOrderSelectOptions = useMemo(
    () => [
      ...manualOrderPrices.map(({ type, accessPeriod, id, currency, unit_amount, customLabel }) => ({
        label: `${t(`products:paymentTypeOptions.${type}`)}${
          type === "recurring" || (type === "one_time" && accessPeriod)
            ? ` (${t(`products:${type}.${accessPeriod?.interval}`, {
                count: accessPeriod?.interval_count,
              })})`
            : ""
        } - ${getProductPrice({ currency, unit_amount })}`.concat(customLabel ? ` - ${customLabel}` : ""),
        value: id,
      })),
      { label: t("client:order.issueManualCustomPriceOption"), value: CUSTOM_PRICE_OPTION },
    ],
    [manualOrderPrices, t],
  );

  const selectedManualOrderPriceId = Form.useWatch("priceId", manualOrderForm);
  const selectedManualOrderPrice = useMemo(
    () => manualOrderPrices.find((price) => price.id === selectedManualOrderPriceId) ?? null,
    [manualOrderPrices, selectedManualOrderPriceId],
  );
  const customPriceSelected = isCustomPriceSelected(selectedManualOrderPriceId);
  const customPriceType = Form.useWatch("customPriceType", manualOrderForm);
  const customPriceInterval = Form.useWatch("customPriceInterval", manualOrderForm);
  const skipImpactOnClientAccess = Form.useWatch("skipImpactOnClientAccess", manualOrderForm);
  const sendClientNotification = Form.useWatch("sendClientNotification", manualOrderForm);

  const fetchClientOrders = useCallback(() => {
    if (clientEmail) {
      void dispatch(
        productClientActions.fetchProductClientOrders({
          productId: productClient.productId,
          clientEmail,
        }),
      );
    }
  }, [clientEmail, dispatch, productClient.productId]);

  useEffect(() => {
    fetchClientOrders();
  }, [fetchClientOrders]);

  if (!productClient) {
    return null;
  }

  const openManualOrderForm = () => {
    const defaultProductId =
      productDetails?.id === productClient.productId &&
      productDetails.chargeType === ProductChargeType.PAID &&
      getActivePaidPrices(productDetails).length > 0
        ? productClient.productId
        : productOptions[0]?.value;

    const defaultProduct =
      defaultProductId === productDetails?.id
        ? productDetails
        : productsListData.find((product) => product.id === defaultProductId);
    const defaultPrice = defaultProduct ? getActivePaidPrices(defaultProduct)[0] : undefined;

    if (!defaultProduct || !defaultPrice) {
      void message.warning(t("client:order.issueManualNoPrices"));
      return;
    }

    manualOrderForm.setFieldsValue({
      productId: defaultProduct.id,
      priceId: defaultPrice.id,
      customLabel: defaultPrice.customLabel || "",
      amount: getPriceAmount(defaultPrice),
      sendClientNotification: true,
      ...customPriceDefaults,
    });
    setShowManualOrderForm(true);
  };

  const handleManualOrderProductChange = (productId: string) => {
    const nextProduct =
      productDetails?.id === productId ? productDetails : productsListData.find((product) => product.id === productId);
    const nextPrice = nextProduct ? getActivePaidPrices(nextProduct)[0] : undefined;

    if (!nextPrice) {
      manualOrderForm.setFieldsValue({
        priceId: undefined as unknown as string,
        customLabel: "",
        amount: 0,
      });
      return;
    }

    manualOrderForm.setFieldsValue({
      priceId: nextPrice.id,
      customLabel: nextPrice.customLabel || "",
      amount: getPriceAmount(nextPrice),
    });
  };

  const handleManualOrderPriceChange = (priceId: string) => {
    const price = manualOrderPrices.find((item) => item.id === priceId);

    if (price) {
      manualOrderForm.setFieldsValue({
        customLabel: price.customLabel || "",
        amount: getPriceAmount(price),
      });
    }
  };

  const issueManualOrder = async (formModel: ManualOrderFormModel) => {
    const withCustomPrice = isCustomPriceSelected(formModel.priceId);
    const selectedPrice = manualOrderPrices.find((price) => price.id === formModel.priceId);
    const manualOrderAmount = Math.round(Number(formModel.amount) * 100);

    if (!clientEmail || (!selectedPrice && !withCustomPrice) || !formModel.productId) {
      void message.warning(t("client:order.issueManualMissingPrice"));
      return;
    }

    if (!Number.isFinite(manualOrderAmount) || manualOrderAmount < 0 || (withCustomPrice && manualOrderAmount === 0)) {
      void message.warning(t("client:order.issueManualInvalidAmount"));
      return;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const key = `issueManualOrder-${productClient.id}`;

    try {
      setIssuingManualOrder(true);
      message.loading({ content: t("client:order.issueManualLoading"), key });

      await productApi.post("/manual-payment/create-order", {
        productId: formModel.productId,
        priceId: withCustomPrice ? null : selectedPrice?.id,
        customPrice: withCustomPrice
          ? buildCustomPricePayload(formModel, getProductCurrency(selectedProductForManualOrder))
          : null,
        clientEmail,
        timezone,
        manualOrderOverrides: {
          amount: manualOrderAmount,
          priceCustomLabel: formModel.customLabel || "",
          sendClientNotification: formModel.sendClientNotification !== false,
          skipImpactOnClientAccess: resolveSkipImpactOnClientAccess(formModel, withCustomPrice),
        },
      });

      message.success({ content: t("client:order.issueManualSuccess"), key });
      setShowManualOrderForm(false);
      manualOrderForm.resetFields();
      fetchClientOrders();
    } catch (e) {
      message.error({
        content: `${t("client:order.issueManualError")}: ${getErrorMessage(e)}`,
        key,
      });
    } finally {
      setIssuingManualOrder(false);
    }
  };

  const getTransactionType = (order: Order) => {
    if (order.orderForImport) {
      return t("client:operationType.clientImport");
    }
    if (order.metadata?.type === "payoff") {
      // Lump-sum payoff of the remaining installments (TRE-1511) — without
      // this branch it would render as a plain "next installment".
      return t("client:operationType.installmentsPayoff");
    }
    if (order.paymentCycle === 1 && order.paymentType === PaymentTypeOption.Recurring && order.installments) {
      if (order.price?.paidPhase) {
        return `${t("client:operationType.firstInstallmentInAdvance")} - (Początek: ${dayjs
          .unix(order.price.paidPhase.start_date)
          .format("DD.MM.YYYY")} - ${order.price.paidPhase.metadata?.totalIterations} rat)`;
      }

      if (order.metadata?.warmupPeriod) {
        return t("client:operationType.warmupPeriod");
      }

      return t("client:operationType.firstInstallment");
    }
    if (
      order.paymentType === OrderPaymentTypeOption.Free ||
      (order.paymentCycle === 1 && order.paymentType === PaymentTypeOption.Recurring)
    ) {
      return t("client:operationType.startOfSubscription");
    }
    if (order.paymentCycle && order.paymentCycle > 1 && order.paymentType === PaymentTypeOption.Recurring) {
      if (
        order.installments ||
        (productClient.accessPeriod?.type === "recurring" &&
          order.connectedSubscription === productClient.accessPeriod.subscriptionId &&
          productClient.accessPeriod.installments)
      ) {
        if (order.metadata?.renewal && order.status === OrderStatusOption.Scheduled) {
          return t("client:operationType.futureAccessRenewal");
        }

        if (order.price?.amount === 0) {
          return t("client:operationType.freeInstallment");
        }

        return t("client:operationType.nextInstallment");
      }
      if (order.price && "baseAmount" in order.price) {
        return t("client:operationType.updateSubscriptionCycle");
      }
      return t("client:operationType.nextSubscriptionCycle");
    }

    if (order.paymentType === PaymentTypeOption.OneTime) {
      return t("client:operationType.oneTimePayment");
    }

    return "-";
  };

  const onPageChange = (nextPage: number) => {
    if (clientEmail) {
      void dispatch(
        productClientActions.fetchProductClientOrdersNextPage({
          productId: productClient.productId,
          clientEmail,
          page: nextPage,
        }),
      );
    }
  };

  return (
    <div>
      <Divider orientation="left">{t("client:actions.title")}</Divider>
      {ownerView && showBillingData ? (
        <div className="mb-4 flex justify-end">
          <Button type="primary" icon={<FileAddOutlined />} onClick={openManualOrderForm} loading={issuingManualOrder}>
            {t("client:order.issueManual")}
          </Button>
        </div>
      ) : null}
      {!clientOrders.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div>{t<string>("common:emptyList")}</div>} />
      ) : (
        <Table<Order>
          loading={ordersStatus === RequestStatus.FETCHING || ordersStatus === RequestStatus.UPDATING}
          rowKey="id"
          dataSource={maxRows ? (clientOrders ?? []).slice(0, maxRows) : (clientOrders ?? [])}
          pagination={
            maxRows
              ? false
              : {
                  current: ordersPage,
                  pageSize: 50,
                  total: Math.max(productClient.connectedOrders.length, clientOrders.length),
                  onChange: onPageChange,
                }
          }
          scroll={{
            x: true,
          }}
          expandedRowRender={
            showBillingData
              ? (order) => (
                  <div className="space-y-4">
                    <InvoiceDataInfo orderClient={order.client} />
                    {customConsents && customConsents.length > 0 && (!order.paymentCycle || order.paymentCycle == 1) ? (
                      <CustomConsentData
                        termsUrl={data?.checkoutSettings?.cart?.pl?.termsUrl}
                        privacyPolicyUrl={data?.checkoutSettings?.cart?.pl?.privacyPolicyUrl}
                        customConsents={customConsents}
                        metadata={order.metadata}
                        allConsents={data?.checkoutSettings?.cart?.pl?.customConsents || []}
                      />
                    ) : null}
                  </div>
                )
              : undefined
          }
        >
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.orderNumber")}
            dataIndex="orderNumber"
            key="orderNumber"
          />

          <Table.Column<Order>
            title={t("client:actions.table.type")}
            dataIndex="type"
            key="type"
            render={(_, row) => getTransactionType(row)}
          />

          <Table.Column<Order>
            title={t("client:actions.table.description")}
            dataIndex="description"
            key="description"
            render={(_, row) => {
              if (row.metadata?.warmupPeriod && row.price && "priceDescription" in row.price) {
                return `${row.price.priceDescription} (Rozszerzenie o dodatkowy okres: ${dayjs
                  .unix(row.metadata.warmupPeriod.startDate)
                  .format("DD.MM.YYYY")} - ${dayjs.unix(row.metadata.warmupPeriod.endDate).format("DD.MM.YYYY")})`;
              }

              return row.price && "priceDescription" in row.price ? row.price.priceDescription || "-" : "-";
            }}
          />
          <Table.Column<Order>
            title={t("client:actions.table.date")}
            dataIndex="createdAt"
            key="createdAt"
            render={(createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
          />
          <Table.Column<Order>
            title={t("client:actions.table.price")}
            dataIndex="price"
            key="price"
            render={(price: Order["price"], order) =>
              price && typeof price.amount === "number" ? (
                <Space>
                  <b className="whitespace-nowrap">{getHumanPrice(price.amount, price.currency)}</b>
                  <CrossSellInfo crossSell={order.crossSell} />
                </Space>
              ) : (
                "-"
              )
            }
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.paymentGateway")}
            dataIndex="paymentGateway"
            key="paymentGateway"
            render={(_paymentGateway: string, order) => <OrderPaymentCell order={toOrderPaymentDescriptor(order)} />}
          />
          <Table.Column<Order>
            title="Kod rabatowy"
            key="appliedCoupon"
            render={(_, order) => {
              // An order can carry several stacked codes — `discounts` holds them
              // all, `discount` is the primary one kept for older orders.
              const seen = new Set<string>();
              const coupons = (order.items ?? [])
                .filter((item) => !item.crossSell)
                .flatMap((item) => (item.discounts?.length ? item.discounts : item.discount ? [item.discount] : []))
                .filter((coupon) => {
                  if (!coupon?.code || seen.has(coupon.code)) {
                    return false;
                  }
                  seen.add(coupon.code);
                  return true;
                });

              if (!coupons.length) {
                return "-";
              }

              const mainItem = order.items?.find((item) => !item.crossSell) ?? null;
              const original = mainItem?.priceAmount ?? null;
              const final = order.price?.amount ?? null;
              const diff = original != null && final != null ? Math.max(0, original - final) : null;
              const currency = order.price?.currency ?? mainItem?.priceCurrency ?? "pln";
              const installments = mainItem?.installments ?? 0;

              // A "once" code is spent on the payment above; a "forever" one keeps
              // coming off every following cycle, which the order total alone does
              // not show — spell both out so a stacked order is readable.
              const recurringCoupons = coupons.filter(couponAppliesToRecurring);
              const recurringDiscount =
                recurringCoupons.length && original != null
                  ? original - applyCouponsToAmount(original, coupons, { recurringOnly: true })
                  : 0;

              const scopeLabel = (coupon: (typeof coupons)[number]) =>
                couponAppliesToRecurring(coupon)
                  ? installments > 1
                    ? "każda rata"
                    : "każda płatność"
                  : installments > 1
                    ? "tylko ta rata"
                    : "tylko ta płatność";

              const valueLabel = (coupon: (typeof coupons)[number]) =>
                coupon.coupon?.percent_off
                  ? `${coupon.coupon.percent_off}%`
                  : coupon.coupon?.amount_off
                    ? getHumanPrice(coupon.coupon.amount_off, currency)
                    : "";

              return (
                <Space direction="vertical" size={2}>
                  <Space size={4} wrap>
                    {coupons.map((coupon) => (
                      <Tooltip
                        key={coupon.code}
                        title={[valueLabel(coupon), scopeLabel(coupon)].filter(Boolean).join(" · ")}
                      >
                        <Tag color="plum" style={{ marginInlineEnd: 0 }}>
                          {coupon.code}
                        </Tag>
                      </Tooltip>
                    ))}
                  </Space>
                  {diff != null && diff > 0 ? (
                    <span className="whitespace-nowrap text-xs text-gray-500">
                      -{getHumanPrice(diff, currency)} {installments > 1 ? "(ta rata)" : "(ta płatność)"}
                    </span>
                  ) : null}
                  {recurringDiscount > 0 ? (
                    <span className="whitespace-nowrap text-xs text-gray-500">
                      -{getHumanPrice(recurringDiscount, currency)}{" "}
                      {installments > 1 ? "(kolejne raty)" : "(kolejne płatności)"}
                    </span>
                  ) : null}
                </Space>
              );
            }}
          />
          <Table.Column<Order>
            title={t("client:actions.table.status")}
            dataIndex="status"
            key="status"
            render={(_type, record) => <OrderStatusCell order={record} />}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.paidAt")}
            key="paidAt"
            render={(_value, record) => <OrderPaidAtCell order={record} />}
          />
          {showBillingData ? (
            <Table.Column<Order>
              title={t("client:actions.table.invoice")}
              dataIndex="connectedInvoice"
              key="connectedInvoice"
              render={(connectedInvoice: Order["connectedInvoice"], order) => (
                <Space>
                  <InvoiceNumberInfo invoice={connectedInvoice} hideError />
                  <IssueInvoice order={order} onInvoiceIssueSuccess={fetchClientOrders} />
                </Space>
              )}
            />
          ) : null}
        </Table>
      )}
      <Modal
        open={showManualOrderForm}
        title={t("client:order.issueManualTitle")}
        okText={t("client:order.issueManual")}
        confirmLoading={issuingManualOrder}
        onCancel={() => setShowManualOrderForm(false)}
        onOk={() => manualOrderForm.submit()}
      >
        <Form<ManualOrderFormModel> form={manualOrderForm} layout="vertical" onFinish={issueManualOrder}>
          <Form.Item name="productId" label={t("client:order.issueManualProduct")} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              options={productOptions}
              onChange={handleManualOrderProductChange}
            />
          </Form.Item>
          <Form.Item name="priceId" label={t("client:order.issueManualPrice")} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              options={manualOrderSelectOptions}
              onChange={handleManualOrderPriceChange}
            />
          </Form.Item>
          <Form.Item name="customLabel" label={t("client:order.issueManualCustomLabel")}>
            <Input />
          </Form.Item>
          <Form.Item
            name="amount"
            label={t("client:order.issueManualAmount")}
            rules={[{ required: true }, { type: "number", min: 0 }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              addonAfter={(
                selectedManualOrderPrice?.currency ?? getProductCurrency(selectedProductForManualOrder)
              ).toUpperCase()}
              style={{ width: "100%" }}
            />
          </Form.Item>
          {customPriceSelected ? (
            <ManualOrderCustomPriceFields
              priceType={customPriceType}
              interval={customPriceInterval}
              skipImpactOnClientAccess={skipImpactOnClientAccess}
            />
          ) : null}
          <Form.Item
            name="sendClientNotification"
            label={t("client:order.issueManualSendClientNotification")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Alert
            showIcon
            type={sendClientNotification === false ? "warning" : "info"}
            message={
              sendClientNotification === false
                ? t("client:order.issueManualNotificationDisabledInfo")
                : t("client:order.issueManualNotificationInfo")
            }
          />
        </Form>
      </Modal>
    </div>
  );
};

export default ProductClientActionsHistory;
